import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { authApi } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/common/Layout/Navbar";
import "./Auth.css";

const ResetPassword = ({ forceChange = false }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams();
  const { updateUser } = useAuth();

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
  });

  // ============================================================
  // Password Validation Functions
  // ============================================================

  const validatePassword = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const isPasswordValid = () => {
    return (
      passwordStrength.length &&
      passwordStrength.uppercase &&
      passwordStrength.lowercase &&
      passwordStrength.digit &&
      passwordStrength.special
    );
  };

  const getPasswordStrengthText = () => {
    if (password.length === 0) return "";

    const validCount = Object.values(passwordStrength).filter(Boolean).length;

    if (validCount === 5) return { text: "Strong", color: "#10b981" };
    if (validCount >= 3) return { text: "Medium", color: "#f59e0b" };
    return { text: "Weak", color: "#ef4444" };
  };

  const getPasswordStrengthPercent = () => {
    const validCount = Object.values(passwordStrength).filter(Boolean).length;
    return (validCount / 5) * 100;
  };

  // ============================================================
  // Toggle Password Visibility
  // ============================================================

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // ============================================================
  // Submit Handler
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!isPasswordValid()) {
      setError("Password does not meet the requirements below.");
      return;
    }

    setLoading(true);

    try {
      if (forceChange) {
        const response = await authApi.changeTemporaryPassword(password);
        updateUser({ MustChangePassword: false });
        setMessage(response.message || "Your password has been changed.");
        setCompleted(true);
        return;
      }

      const response = await authApi.resetPassword(token, password);

      setMessage(response.message || "Password reset successfully!");

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar variant="auth" />
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>{forceChange ? "Choose a new password" : "Reset Password"}</h1>
            <p>
              {forceChange
                ? "For your security, replace your temporary password before continuing."
                : "Enter your new password"}
            </p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {message && <div className="alert alert-success">{message}</div>}

          {completed ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate("/dashboard", { replace: true })}
            >
              Continue to dashboard
            </button>
          ) : (
          <form onSubmit={handleSubmit}>
            {/* New Password with Toggle */}
            <div className="form-group password-group">
              <label htmlFor="password">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePassword(e.target.value);
                  }}
                  required
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  minLength={8}
                  className={
                    password.length > 0
                      ? isPasswordValid()
                        ? "valid"
                        : "invalid"
                      : ""
                  }
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  tabIndex="-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i
                    className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
                  ></i>
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password.length > 0 && (
                <div className="password-strength-container">
                  <div className="password-strength-bar">
                    <div
                      className="password-strength-fill"
                      style={{
                        width: `${getPasswordStrengthPercent()}%`,
                        background: getPasswordStrengthText().color,
                      }}
                    />
                  </div>
                  <div className="password-strength-text">
                    <span style={{ color: getPasswordStrengthText().color }}>
                      {getPasswordStrengthText().text}
                    </span>
                  </div>
                </div>
              )}

              {/* Password Requirements Checklist */}
              <div className="password-requirements">
                <ul>
                  <li className={passwordStrength.length ? "met" : "unmet"}>
                    <span className="requirement-icon">
                      {passwordStrength.length ? "✓" : "○"}
                    </span>
                    At least 8 characters
                  </li>
                  <li className={passwordStrength.uppercase ? "met" : "unmet"}>
                    <span className="requirement-icon">
                      {passwordStrength.uppercase ? "✓" : "○"}
                    </span>
                    At least 1 uppercase letter
                  </li>
                  <li className={passwordStrength.lowercase ? "met" : "unmet"}>
                    <span className="requirement-icon">
                      {passwordStrength.lowercase ? "✓" : "○"}
                    </span>
                    At least 1 lowercase letter
                  </li>
                  <li className={passwordStrength.digit ? "met" : "unmet"}>
                    <span className="requirement-icon">
                      {passwordStrength.digit ? "✓" : "○"}
                    </span>
                    At least 1 number
                  </li>
                  <li className={passwordStrength.special ? "met" : "unmet"}>
                    <span className="requirement-icon">
                      {passwordStrength.special ? "✓" : "○"}
                    </span>
                    At least 1 special character (!@#$%^&*)
                  </li>
                </ul>
              </div>
            </div>

            {/* Confirm Password with Toggle */}
            <div className="form-group password-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  minLength={8}
                  className={
                    confirmPassword.length > 0
                      ? password === confirmPassword && isPasswordValid()
                        ? "valid"
                        : "invalid"
                      : ""
                  }
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  tabIndex="-1"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  <i
                    className={
                      showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"
                    }
                  ></i>
                </button>
              </div>
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <span className="password-match-error">
                  Passwords do not match
                </span>
              )}
              {confirmPassword.length > 0 &&
                password === confirmPassword &&
                isPasswordValid() && (
                  <span className="password-match-success">
                    ✓ Passwords match
                  </span>
                )}
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={
                loading || !isPasswordValid() || password !== confirmPassword
              }
            >
              {loading
                ? forceChange
                  ? "Updating..."
                  : "Resetting..."
                : forceChange
                  ? "Set new password"
                  : "Reset Password"}
            </button>
          </form>
          )}

          {!forceChange && (
            <div className="auth-footer">
              <p>
                <Link to="/login">Back to Sign In</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
